import memoize from 'lodash/memoize'
// eslint-disable-next-line no-restricted-imports
import {toggle} from 'opticks'

import {logger} from '../../utils/logging/logger'
import {useIsHydrated} from '../../utils/react/hooks/useIsHydrated'
import {useServerExperimentContext} from '../context/ServerExperimentsContext'
import {createUniversalToggle} from '../utils/createUniversalToggle'

import type {Toggle} from 'types/Toggle'

const memoizedUniversalToggle = memoize(createUniversalToggle)

const opticksToggle: Toggle = (...args) => toggle(...args)
opticksToggle.displayName = 'OpticksToggle'

/**
 * Hook that provides access to the opticks toggle function with server-side experiment support.
 *
 * Decisions are only tracked..
 * - client-side
 * - *after* hydration on cached pages
 *
 * Mismatch tracking between preloaded and client decisions is handled in OpticksProvider.
 * @returns A toggle function like opticks
 */
export const useToggle = (): Toggle => {
  const {isCachedPage, serverToggle, presetExperiments} =
    useServerExperimentContext()
  const isHydrated = useIsHydrated()

  // Hydrated client can use toggle singleton
  if (isHydrated) return opticksToggle

  // Cached initial client/server render uses preset (lambda) decisions or A side
  if (isCachedPage) return memoizedUniversalToggle(presetExperiments)

  // Uncached initial client-render
  if (typeof window !== 'undefined') return opticksToggle

  // Uncached initial server-render
  if (serverToggle) return serverToggle

  // Technically unreachable fallback, we only use uncached pages in `client` where serverToggle is set up
  // this could cause hydration mismatches and flashes of the wrong variation if it occurs
  logger.error('useToggle was called during server-render without serverToggle')
  return memoizedUniversalToggle(presetExperiments)
}
